// Define a service using a base URL and expected endpoints
// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { apiConfig } from '../../config'
import { ObjectValueChangedNotification, CreateObjectInstanceCommand, UpdateObjectInstanceCommand, GetObjectByIdArgs, GetObjectChildrenArgs, ObjectSchemFromParentQueryArgs, DeleteObjectArgs, ObjectSchemFromParentMultiQueryArgs, ObjectRelationsQueryArgs, ObjectHierarchyQueryArgs, RelationsConfiguration, CreateRelationsCommand, MoveObjectCommand, DeleteRelationsCommand, GetMatchArgs } from './types'
import { Recipe } from '@reduxjs/toolkit/dist/query/core/buildThunks'
import { getHeaders, ApiScope } from '../silentAuth'
import { ObjectsHubConnector } from './objectsHub'
import { cacher } from "../rtkQueryCacheUtils";

const hub = ObjectsHubConnector.getInstance();

export const objectsApi = createApi({
    reducerPath: 'objectsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: apiConfig.objectsApiUrl,
        prepareHeaders: async (headers, { getState, forced }) => {
            // if (forced) headers.set("Cache-Control", "max-age=0, must-revalidate")
            return await getHeaders(ApiScope, headers);
        }
    }),
    tagTypes: [...cacher.defaultTags, 'Objects'],
    refetchOnReconnect: true,
    endpoints: (builder) => ({
        getObjects: builder.query<any[], void>({
            query: () => `v2/objects/`,
            transformResponse: (response: { data: any[] }, meta, arg) => response.data,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getObjectsWithModel: builder.query<any[], { twinModelId: string }>({
            query: ({ twinModelId }) => `v2/objects?model=${twinModelId}`,
            transformResponse: (response: { data: any[] }, meta, arg) => response.data,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getObjectsInResourceGroup: builder.query<any[], { resourceGroupId: string, queryParameters: string }>({
            query: ({ resourceGroupId, queryParameters }) => `v2/objects/${resourceGroupId}?${queryParameters}`,
            transformResponse: (response: { data: any[] }, meta, arg) => response.data,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getObjectById: builder.query<any, GetObjectByIdArgs>({
            query: ({ resourceGroupId, id }) => `v2/objects/${resourceGroupId}/${id}`,
            providesTags: (result, error, { id }) => [{ type: 'Objects', id }],
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValueChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getObjectsChildren: builder.query<any[], GetObjectChildrenArgs>({
            query: ({ resourceGroupId, id: objectId }) => `v2/objects/${resourceGroupId}/${objectId}/children`,
            transformResponse: (response: { data: any[] }, meta, arg) => response.data,
            providesTags: (result, error, args) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'Objects' as const, id })), 'Objects']
                    : ['Objects'],
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getObjectsWithBim: builder.query<any[], void>({
            query: () => `v2/objects?bimid`,
            transformResponse: (response: { data: any[] }, meta, arg) => response.data,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getObjectsBySchemaFromParent: builder.query<any[], ObjectSchemFromParentQueryArgs>({
            query: ({ resourceGroupId, schema, objectId }) => `v2/objects/${resourceGroupId}/${objectId}/schema/${schema}`, //v2/objects/{resourceGroupId}/{id}/schema/{schema}
            providesTags: cacher.providesList('Objects'),
            transformResponse: (response: { data: any[] }, meta, arg) => response.data,
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getMultiObjectsBySchemaFromParent: builder.query<any[], ObjectSchemFromParentMultiQueryArgs>({
            providesTags: cacher.providesList('Objects'),
            queryFn: (args: ObjectSchemFromParentMultiQueryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
                const promises = args.items.map((elm) => {
                    return fetchWithBQ({ url: `v2/objects/${elm.resourceGroupId}/${elm.objectId}/schema/${elm.schema}` });
                });
                return Promise.all(promises).then((results) => {
                    return {
                        data: results?.map((result, index) => {
                            return result.data ? {
                                items: (result.data as any).data, args: args.items[index]
                            } : { error: result.error }
                        })
                    }; // final result must have either the property "data" or the property "error"
                });
            },
        },
        ),
        getMatch: builder.query<any[], GetMatchArgs>({
            query: ({ resourceGroupId, objectId, relationship, hops, queryParameters, modelId }: GetMatchArgs) => {
                const url = `v2/objects/${resourceGroupId}/${objectId}/match`;
                const prms: string[] = [];
                if (hops) prms.push(`hops=${hops}`);
                if (relationship) prms.push(`relationship=${relationship}`);
                if (modelId) prms.push(`modelId=${modelId}`);
                if (queryParameters) prms.push(queryParameters);
                return url + `?${prms.join('&')}`;
            },
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        updateObject: builder.mutation<any, UpdateObjectInstanceCommand>({
            query: (obj) => ({
                url: `/v2/objects/${obj.resourceGroupid}/${obj.objectId}`,
                method: 'post',
                body: obj
            }),
            // we get notifications by SingalR - so we shouldn't need this
            //invalidatesTags: (result, error, { objectId }) => [{ type: 'Objects' as const, objectId }]
        }),
        deleteObject: builder.mutation<any, DeleteObjectArgs>({
            query: ({ id, resourceGroupId }) => ({
                url: `/v2/objects/${resourceGroupId}/${id}`,
                method: 'delete',
            }),
            invalidatesTags: cacher.invalidatesList("Objects")
        }),
        createObject: builder.mutation<any, CreateObjectInstanceCommand>({
            query: (obj) => ({
                url: `/v2/objects/${obj.resourceGroupId}`,
                method: 'post',
                body: obj
            }),
            invalidatesTags: cacher.invalidatesList("Objects")
        }),

        cloneObject: builder.mutation<any, {resourceGroupId: string, id: string, newName: string, newDescription: string, recursive: boolean, includeProperties: boolean}>({
            query: (obj) => ({
                url: `/v2/objects/${obj.resourceGroupId}/${obj.id}/clone`,
                method: 'post',
                body: obj
            }),
            invalidatesTags: cacher.invalidatesList("Objects")
        }),

        // relations
        getRelationsInById: builder.query<any[], ObjectRelationsQueryArgs>({
            query: ({ objectId, type, resourceGroupId }) => `/v2/objectrelations/${resourceGroupId}/${objectId}/in/${type}`,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getRelationsOutById: builder.query<any[], ObjectRelationsQueryArgs>({
            query: ({ objectId, type, resourceGroupId }) => `/v2/objectrelations/${resourceGroupId}/${objectId}/out/${type}`,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getRelationsBothById: builder.query<any[], ObjectRelationsQueryArgs>({
            query: ({ objectId, type, resourceGroupId }) => `/v2/objectrelations/${resourceGroupId}/${objectId}/both/${type}`,
            providesTags: cacher.providesList('Objects'),
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) { await subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) },
        }),
        getHierarchyById: builder.query<any[], ObjectHierarchyQueryArgs>({
            query: ({ objectId, resourceGroupId }) => `/v2/objectrelations/${resourceGroupId}/${objectId}/hierarchy`,
            providesTags: cacher.providesList('Objects'),
        }),
        createRelation: builder.mutation<RelationsConfiguration, CreateRelationsCommand>({
            query: (obj) => ({
                url: `/v2/objectrelations/${obj.resourceGroupId}`,
                method: 'put',
                body: obj
            }),
            invalidatesTags: cacher.invalidatesList("Objects")
        }),
        moveObject: builder.mutation<any, MoveObjectCommand>({
            query: (obj) => ({
                url: `/v2/objectrelations/${obj.resourceGroupId}/move`,
                method: 'post',
                body: obj
            }),
            invalidatesTags: cacher.invalidatesList("Objects")
        }),
        deleteRelation: builder.mutation<RelationsConfiguration, DeleteRelationsCommand>({
            query: (obj) => ({
                url: `/v2/objectrelations/${obj.resourceGroupId}`,
                method: 'delete',
                body: obj
            }),
            invalidatesTags: cacher.invalidatesList("Objects")
        }),
    })
})

const onValueChanged = (updateCachedData: Recipe<any>, message: ObjectValueChangedNotification) => {
    //console.debug('Received RAW --->',message)
    updateCachedData((draft: any) => {
        const elm = draft?.find((a: any) => a.id === message.objectId);
        if (elm) {
            updateItem(elm, message.propertyName, message.value, message.objectId);
        }
    })
}

const onSingleValueChanged = (updateCachedData: Recipe<any>, message: ObjectValueChangedNotification) => {
    //console.debug('Received RAW --->' + message.objectId + '/' + message.propertyName + '  Value: ' + message.value)
    updateCachedData((draft: any) => {
        const elm = draft;
        if (elm && elm.id === message.objectId) {
            updateItem(elm, message.propertyName, message.value, message.objectId);
        }
    })
}

const updateItem = (elm: any, property: string, value: any, objectId: string) => {
    if (property.includes('/')) {
        const names = property.split('/')
        elm[firstCharToLower(names[0])][firstCharToLower(names[1])] = value;
    }
    else
        elm[firstCharToLower(property)] = value;

    //console.debug('Updated --->' + objectId + '/' + property + '  Value: ' + value)
}
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetObjectsQuery,
    useGetObjectByIdQuery,
    useGetObjectsInResourceGroupQuery,
    useGetObjectsChildrenQuery,
    useCreateObjectMutation,
    useUpdateObjectMutation,
    useDeleteObjectMutation,
    useGetObjectsWithBimQuery,
    useGetObjectsBySchemaFromParentQuery,
    useGetMultiObjectsBySchemaFromParentQuery,
    useGetObjectsWithModelQuery,
    useGetRelationsInByIdQuery,
    useGetRelationsOutByIdQuery,
    useGetRelationsBothByIdQuery,
    useGetHierarchyByIdQuery,
    useCreateRelationMutation,
    useDeleteRelationMutation,
    useMoveObjectMutation,
    useLazyGetRelationsOutByIdQuery,
    useGetMatchQuery,
    useCloneObjectMutation
} = objectsApi


const firstCharToLower = (str: string) => {
    return str.charAt(0).toLowerCase() + str.slice(1)
}

async function subscribeForLiveValuesChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) {
    // create a websocket connection when the cache subscription starts
    let res;
    try {
        // wait for the initial query to resolve before proceeding
        res = await cacheDataLoaded;
        res.data.map((v) => hub.on(v.id, (message: ObjectValueChangedNotification) => onValueChanged(updateCachedData, message)));
    } catch {
        // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        // in which case `cacheDataLoaded` will throw
    }
    // cacheEntryRemoved will resolve when the cache subscription is no longer active
    await cacheEntryRemoved
    // perform cleanup steps once the `cacheEntryRemoved` promise resolves
    res?.data?.map((v) => hub.off(v.id, (message: ObjectValueChangedNotification) => onValueChanged(updateCachedData, message)));
}

async function subscribeForLiveValueChanges(updateCachedData, cacheDataLoaded, cacheEntryRemoved) {
    // create a websocket connection when the cache subscription starts
    let res;
    try {
        // wait for the initial query to resolve before proceeding
        res = await cacheDataLoaded;
        hub.on(res.data.id, (message: ObjectValueChangedNotification) => onSingleValueChanged(updateCachedData, message));
    } catch {
        // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        // in which case `cacheDataLoaded` will throw
    }
    // cacheEntryRemoved will resolve when the cache subscription is no longer active
    await cacheEntryRemoved


    // perform cleanup steps once the `cacheEntryRemoved` promise resolves
    hub.off(res.data.id, (message: ObjectValueChangedNotification) => onSingleValueChanged(updateCachedData, message));
    // perform cleanup steps once the `cacheEntryRemoved` promise resolves
}